/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 水印内容照片列表状态管理
 */
import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit'
import { Modal } from 'antd'
import { RootState } from '../app/store'
import { capturePromiseException } from '../utils/sentryUtils'
import { buildParamsWithAuthPolicy } from '../actions/photo'
import {
  Photo,
  ExpandInfo,
  FetchPhotosParams,
  FetchPhotosResponse
} from '../pages/ai-photo-king/types'
import fetchHelper from '../utils/fetchHelper'
import { enableMapSet } from 'immer'

enableMapSet()

// 声明全局变量（Vite define 注入）
declare const __API_SERVER_HOST__: string

// 定义初始状态类型
interface PhotosByContentState {
  // 照片列表
  photos: Photo[]
  photosNum: number
  lastPageCond: string

  // 扩展信息
  expandInfo: ExpandInfo | null

  // 选中状态
  checkedPhotos: Set<number>
  unCheckedPhotos: Set<number>
  checkAll: boolean

  // 其他状态
  canFavorite: boolean
  loading: boolean
  isGroupPhotoViewLimit: boolean
  oldPhotosCount: number

  // 排序状态（按水印内容）
  isAscSort: boolean

  // 猜你想搜相关
  suggestedWords: Array<{ word: string; count: number; isChecked: boolean }>
  suggestedPhotos: Photo[]
  suggestedPhotosNum: number
  suggestedLastPageCond: string

  // 无结果推荐策略 (P1/P2/P3)
  fallbackType: 'none' | 'P1' | 'P2' | 'P3'
  fallbackKeyword: string

  // 猜你想搜/无结果推荐 实际使用的查询条件（保存放宽后的条件）
  suggestedQueryFilter: any | null

  // 猜你想搜选中状态 (独立于主列表)
  suggestedCheckedPhotos: Set<number>
  suggestedUnCheckedPhotos: Set<number>
  suggestedCheckAll: boolean
}

// 初始状态
const initialState: PhotosByContentState = {
  photos: [],
  photosNum: 0,
  lastPageCond: '',
  expandInfo: null,
  checkedPhotos: new Set<number>(),
  unCheckedPhotos: new Set<number>(),
  checkAll: false,
  canFavorite: false,
  loading: false,
  isGroupPhotoViewLimit: false,
  oldPhotosCount: 0,
  isAscSort: false,

  // 猜你想搜初始状态
  suggestedWords: [],
  suggestedPhotos: [],
  suggestedPhotosNum: 0,
  suggestedLastPageCond: '',
  fallbackType: 'none',
  fallbackKeyword: '',
  suggestedQueryFilter: null,
  suggestedCheckedPhotos: new Set<number>(),
  suggestedUnCheckedPhotos: new Set<number>(),
  suggestedCheckAll: false
}

/**
 * 获取照片列表（首次加载）
 */
export const fetchPhotosV2 = createAsyncThunk<
  FetchPhotosResponse,
  FetchPhotosParams,
  { state: RootState; rejectValue: string }
>(
  'photosByContent/fetchPhotos',
  async (params, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState()
      const config = {
        url: __API_SERVER_HOST__ + '/v5/workgroup/photos',
        arguments: {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(buildParamsWithAuthPolicy(state, params))
        }
      }

      const result = await fetchHelper(dispatch, getState, config)

      const { payload, status } = result

      if (status === 0) {
        return {
          photos: payload.photos || [],
          expandInfo: payload.expandInfo || null,
          lastPageCond: payload.lastPageCond || '',
          total: payload.total || 0,
          canSetFavorite: payload.canSetFavorite,
          isGroupPhotoViewLimit: payload.isGroupPhotoViewLimit,
          suggestedSearchWords: payload.suggestedSearchWords || []
        }
      } else if (status === -3) {
        Modal.warning({
          title: '提示',
          content: '您已退出或被移出当前群',
          onOk: () => {
            window.location.href = '/'
          }
        })
        return rejectWithValue('您已退出或被移出当前群')
      } else if (status === -34) {
        Modal.warning({
          title: '提示',
          content:
            '照片搜索服务正在升级中，预计2月20日升级完成。升级期间的照片数据会安全保存，不会丢失。'
        })
        return rejectWithValue('照片搜索服务正在升级中')
      } else if (status === -991) {
        Modal.warning({
          title: '提示',
          content: '暂无照片查看权限'
        })
        return {
          photos: [],
          expandInfo: null,
          lastPageCond: '',
          total: 0,
          canSetFavorite: false,
          isGroupPhotoViewLimit: false
        }
      }

      return rejectWithValue('请求失败')
    } catch (error) {
      capturePromiseException(error)
      return rejectWithValue('网络错误')
    }
  }
)

/**
 * 加载更多照片
 */
export const fetchMorePhotosV2 = createAsyncThunk<
  { photos: Photo[]; lastPageCond: string },
  FetchPhotosParams,
  { state: RootState; rejectValue: string }
>(
  'photosByContent/fetchMorePhotos',
  async (params, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState()
      const config = {
        url: __API_SERVER_HOST__ + '/v5/workgroup/photos',
        arguments: {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(buildParamsWithAuthPolicy(state, params))
        }
      }

      const result = await fetchHelper(dispatch, getState, config)

      const { payload, status } = result

      if (status === 0) {
        return {
          photos: payload.photos || [],
          lastPageCond: payload.lastPageCond || ''
        }
      } else if (status === -3) {
        Modal.warning({
          title: '提示',
          content: '您已退出或被移出当前群',
          onOk: () => {
            window.location.href = '/'
          }
        })
        return rejectWithValue('您已退出或被移出当前群')
      } else if (status === -991) {
        Modal.warning({
          title: '提示',
          content: '暂无照片查看权限'
        })
        return rejectWithValue('暂无照片查看权限')
      }

      return rejectWithValue('请求失败')
    } catch (error) {
      capturePromiseException(error)
      return rejectWithValue('网络错误')
    }
  }
)

/**
 * 获取猜你想搜照片列表
 */
export const fetchSuggestedPhotosV2 = createAsyncThunk<
  { photos: Photo[]; total: number; lastPageCond: string },
  FetchPhotosParams,
  { state: RootState; rejectValue: string }
>(
  'photosByContent/fetchSuggestedPhotos',
  async (params, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState()
      const config = {
        url: __API_SERVER_HOST__ + '/v5/workgroup/photos',
        arguments: {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(buildParamsWithAuthPolicy(state, params))
        }
      }

      const result = await fetchHelper(dispatch, getState, config)
      const { payload, status } = result

      if (status === 0) {
        return {
          photos: payload.photos || [],
          total: payload.total || 0,
          lastPageCond: payload.lastPageCond || ''
        }
      }
      return rejectWithValue('请求失败')
    } catch (error) {
      capturePromiseException(error)
      return rejectWithValue('网络错误')
    }
  }
)

/**
 * 加载更多猜你想搜照片
 */
export const fetchMoreSuggestedPhotosV2 = createAsyncThunk<
  { photos: Photo[]; lastPageCond: string },
  FetchPhotosParams,
  { state: RootState; rejectValue: string }
>(
  'photosByContent/fetchMoreSuggestedPhotos',
  async (params, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState()
      const config = {
        url: __API_SERVER_HOST__ + '/v5/workgroup/photos',
        arguments: {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(buildParamsWithAuthPolicy(state, params))
        }
      }

      const result = await fetchHelper(dispatch, getState, config)
      const { payload, status } = result

      if (status === 0) {
        return {
          photos: payload.photos || [],
          lastPageCond: payload.lastPageCond || ''
        }
      }
      return rejectWithValue('请求失败')
    } catch (error) {
      capturePromiseException(error)
      return rejectWithValue('网络错误')
    }
  }
)

/**
 * 获取90天前的照片数量
 */
export const fetch90DaysPhotosCountV2 = createAsyncThunk<
  { count: number; isVip: number; photoImageList: string[] },
  FetchPhotosParams,
  {
    state: RootState
    rejectValue: { count: number; isVip: number; photoImageList: string[] }
  }
>(
  'photosByContent/fetch90DaysPhotosCount',
  async (params, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState()
      const config = {
        url: __API_SERVER_HOST__ + '/v5/workgroup/photos/notvip/count',
        arguments: {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(buildParamsWithAuthPolicy(state, params))
        }
      }

      const result = await fetchHelper(dispatch, getState, config)

      // API 返回格式: { status: 0, isVip: 1, photoImageList: [], count: 123 }
      // 或者: { payload: { status: 0, ... }, status: 0 }
      let data: any = result
      if (
        result &&
        typeof result === 'object' &&
        'payload' in result &&
        'status' in result
      ) {
        // 如果返回格式是 { payload: {...}, status: 0 }
        data = result.payload || result
      }

      if (data && data.status === 0) {
        return {
          count: data.count || data.photoCount || 0,
          isVip: data.isVip || 0,
          photoImageList: data.photoImageList || []
        }
      }

      return rejectWithValue({ count: 0, isVip: 0, photoImageList: [] })
    } catch (error) {
      capturePromiseException(error)
      return rejectWithValue({ count: 0, isVip: 0, photoImageList: [] })
    }
  }
)

// 创建 slice
const photosByContentSlice = createSlice({
  name: 'photosByContent',
  initialState,
  reducers: {
    setIsAscSort: (state, action: PayloadAction<boolean>) => {
      state.isAscSort = action.payload
    },
    // 设置照片列表
    setPhotos: (state, action: PayloadAction<Photo[]>) => {
      state.photos = action.payload
    },
    // 更新收藏状态
    updatePhotoFavorite: (
      state,
      action: PayloadAction<{ seq: number; isFavorite: boolean }>
    ) => {
      const target = state.photos.find(
        (photo) => photo.seq === action.payload.seq
      )
      if (target) {
        target.isFavorite = action.payload.isFavorite
      }
    },
    // 追加照片（用于加载更多）
    appendPhotos: (state, action: PayloadAction<Photo[]>) => {
      state.photos.push(...action.payload)
    },
    // 设置扩展信息
    setExpandInfo: (state, action: PayloadAction<ExpandInfo | null>) => {
      state.expandInfo = action.payload
    },
    // 设置照片数量
    setPhotosNum: (state, action: PayloadAction<number>) => {
      state.photosNum = action.payload
    },
    // 设置分页条件
    setLastPageCond: (state, action: PayloadAction<string>) => {
      state.lastPageCond = action.payload
    },
    // 设置是否可以收藏
    setCanFavorite: (state, action: PayloadAction<boolean>) => {
      state.canFavorite = action.payload
    },
    // 设置是否为大团队照片查看限制
    setIsGroupPhotoViewLimit: (state, action: PayloadAction<boolean>) => {
      state.isGroupPhotoViewLimit = action.payload
    },
    // 设置90天前照片数量
    setOldPhotosCount: (state, action: PayloadAction<number>) => {
      state.oldPhotosCount = action.payload
    },
    /**
     * 批量打标后乐观更新.
     * - affectedSeqs: 受影响的 photo seq 列表 (按 include/exclude 算好后传入)
     * - newLabels: 本次新打的标签 (id/name/color)
     * - exclusive (可选): 跟后端 bind 的 exclusive 字段对齐.
     *   * false / undefined (默认): append 模式, 跟已有 labels 去重后追加
     *   * true: replace 模式, 把命中 seq 的 photo.labels 直接覆盖成 newLabels
     *           (用于「一张照片只能 1 个标签」的产品约束)
     * 用途: 批量打标的 applyTagsHandler / 单照片打标后, 让 UI 立即刷新而不等下次 fetch
     */
    appendLabelsToPhotos: (
      state,
      action: PayloadAction<{
        affectedSeqs: number[]
        newLabels: Array<{ id: string | number; name: string; color?: string }>
        exclusive?: boolean
      }>
    ) => {
      const { affectedSeqs, newLabels, exclusive } = action.payload
      if (!affectedSeqs.length || !newLabels.length) return
      const seqSet = new Set(affectedSeqs)
      for (let i = 0; i < state.photos.length; i++) {
        const p = state.photos[i] as any
        if (!seqSet.has(p.seq)) continue
        if (exclusive) {
          // 覆盖模式: photo.labels = newLabels (按入参顺序, 不去重 — 调用方应已去重)
          ;(state.photos[i] as any).labels = newLabels.map((l) => ({ ...l }))
          continue
        }
        const existing: any[] = Array.isArray(p.labels) ? p.labels : []
        const existingIds = new Set(existing.map((l) => String(l.id)))
        const toAdd = newLabels.filter((l) => !existingIds.has(String(l.id)))
        if (toAdd.length === 0) continue
        ;(state.photos[i] as any).labels = [...existing, ...toAdd]
      }
    },
    /**
     * 跨照片乐观更新.
     * - renamed: id → 新 name/color, 命中的 label 改名 / 改色
     * - deletedIds: 把命中的 label 从 photo.labels 移除
     * - affectedSeqs (可选): 限定只动这些 photo seq;不传 = 全表
     *   * 「管理团队标签」改名/删除标签 → 不传 affectedSeqs (全表都受影响)
     *   * 「批量移除标签」(N 张选中) → 传 affectedSeqs (避免误伤非选中的)
     *   * 单照片 chip × 移除 → Photo 组件内 localLabels 自管, 不走本 reducer
     * (停用 disabled 不在这里处理 — 历史 label 仍要展示, 只是新打标时不下发)
     */
    updateLabelsAcrossPhotos: (
      state,
      action: PayloadAction<{
        renamed?: Array<{
          id: string | number
          name: string
          color?: string
        }>
        deletedIds?: Array<string | number>
        affectedSeqs?: number[]
      }>
    ) => {
      const { renamed = [], deletedIds = [], affectedSeqs } = action.payload
      if (renamed.length === 0 && deletedIds.length === 0) return
      const renameMap = new Map<string, { name: string; color?: string }>()
      for (const r of renamed)
        renameMap.set(String(r.id), { name: r.name, color: r.color })
      const deleteSet = new Set(deletedIds.map((x) => String(x)))
      const seqSet =
        affectedSeqs && affectedSeqs.length > 0 ? new Set(affectedSeqs) : null

      for (let i = 0; i < state.photos.length; i++) {
        const p = state.photos[i] as any
        if (seqSet && !seqSet.has(p.seq)) continue
        const labels: any[] = Array.isArray(p.labels) ? p.labels : []
        if (labels.length === 0) continue
        let changed = false
        const next: any[] = []
        for (const l of labels) {
          const lid = String(l.id)
          if (deleteSet.has(lid)) {
            changed = true
            continue
          }
          const rn = renameMap.get(lid)
          if (rn) {
            const newColor = rn.color || l.color
            if (rn.name !== l.name || newColor !== l.color) {
              changed = true
              next.push({ ...l, name: rn.name, color: newColor })
              continue
            }
          }
          next.push(l)
        }
        if (changed) {
          ;(state.photos[i] as any).labels = next
        }
      }
    },
    // 更新照片整改状态（迁移自 photo.js）
    setPhotoRectification: (
      state,
      action: PayloadAction<{
        photoUrl?: string
        photoRectification?: any
      }>
    ) => {
      const { photoUrl, photoRectification } = action.payload || {}
      if (!photoUrl || !state.photos.length) return
      const cleanPhotoUrl = photoUrl.split('?')[0]

      for (let i = 0; i < state.photos.length; i++) {
        const photo = state.photos[i]
        const cleanLargeURL = (photo.largeURL || '').split('?')[0]
        const cleanSmallURL = (photo.smallURL || '').split('?')[0]
        const cleanVideoURL = (photo.videoURL || '').split('?')[0]

        if (
          cleanLargeURL === cleanPhotoUrl ||
          cleanSmallURL === cleanPhotoUrl ||
          cleanVideoURL === cleanPhotoUrl
        ) {
          state.photos[i] = {
            ...photo,
            photoRectification
          }
          break
        }
      }
    },
    // 选中状态管理
    pushCheckedPhoto: (state, action: PayloadAction<number>) => {
      state.checkedPhotos.add(action.payload)
    },
    removeCheckedPhoto: (state, action: PayloadAction<number>) => {
      state.checkedPhotos.delete(action.payload)
    },
    pushUncheckedPhoto: (state, action: PayloadAction<number>) => {
      state.unCheckedPhotos.add(action.payload)
    },
    removeUncheckedPhoto: (state, action: PayloadAction<number>) => {
      state.unCheckedPhotos.delete(action.payload)
    },
    setCheckAll: (state) => {
      state.checkAll = true
      state.checkedPhotos.clear()
      state.unCheckedPhotos.clear()
    },
    clearCheck: (state) => {
      state.unCheckedPhotos.clear()
      state.checkedPhotos.clear()
      state.checkAll = false
    },
    // 猜你想搜相关 reducers
    setSuggestedWords: (
      state,
      action: PayloadAction<
        Array<{ word: string; count: number; isChecked: boolean }>
      >
    ) => {
      state.suggestedWords = action.payload
    },
    updateSuggestedWordChecked: (
      state,
      action: PayloadAction<{ index: number; isChecked: boolean }>
    ) => {
      if (state.suggestedWords[action.payload.index]) {
        state.suggestedWords[action.payload.index].isChecked =
          action.payload.isChecked
      }
    },
    pushSuggestedCheckedPhoto: (state, action: PayloadAction<number>) => {
      state.suggestedCheckedPhotos.add(action.payload)
    },
    removeSuggestedCheckedPhoto: (state, action: PayloadAction<number>) => {
      state.suggestedCheckedPhotos.delete(action.payload)
    },
    pushSuggestedUncheckedPhoto: (state, action: PayloadAction<number>) => {
      state.suggestedUnCheckedPhotos.add(action.payload)
    },
    removeSuggestedUncheckedPhoto: (state, action: PayloadAction<number>) => {
      state.suggestedUnCheckedPhotos.delete(action.payload)
    },
    setSuggestedCheckAll: (state, action: PayloadAction<boolean>) => {
      state.suggestedCheckAll = action.payload
      state.suggestedCheckedPhotos.clear()
      state.suggestedUnCheckedPhotos.clear()
    },
    clearSuggestedCheck: (state) => {
      state.suggestedCheckedPhotos.clear()
      state.suggestedUnCheckedPhotos.clear()
      state.suggestedCheckAll = false
    },
    // 无结果推荐策略相关 reducers
    setFallbackType: (
      state,
      action: PayloadAction<'none' | 'P1' | 'P2' | 'P3'>
    ) => {
      state.fallbackType = action.payload
    },
    setFallbackKeyword: (state, action: PayloadAction<string>) => {
      state.fallbackKeyword = action.payload
    },
    setSuggestedQueryFilter: (state, action: PayloadAction<any>) => {
      state.suggestedQueryFilter = action.payload
    },
    setSuggestedPhotos: (state, action: PayloadAction<Photo[]>) => {
      state.suggestedPhotos = action.payload
    },
    // 重置状态
    reset: (state) => {
      state.photos = []
      state.photosNum = 0
      state.lastPageCond = ''
      state.expandInfo = null
      state.checkedPhotos.clear()
      state.unCheckedPhotos.clear()
      state.checkAll = false
      state.loading = false
      state.oldPhotosCount = 0
      state.isAscSort = false

      // 重置猜你想搜
      state.suggestedWords = []
      state.suggestedPhotos = []
      state.suggestedPhotosNum = 0
      state.suggestedLastPageCond = ''
      state.fallbackType = 'none'
      state.fallbackKeyword = ''
      state.suggestedQueryFilter = null
      state.suggestedCheckedPhotos.clear()
      state.suggestedUnCheckedPhotos.clear()
      state.suggestedCheckAll = false
    }
  },
  extraReducers: (builder) => {
    builder
      // 处理 fetchPhotosV2
      .addCase(fetchPhotosV2.pending, (state) => {
        state.loading = true
      })
      .addCase(fetchPhotosV2.fulfilled, (state, action) => {
        if (action.payload) {
          state.photos = action.payload.photos || []
          state.expandInfo = action.payload.expandInfo || null
          state.lastPageCond = action.payload.lastPageCond || ''
          state.photosNum = action.payload.total || 0
          state.canFavorite = action.payload.canSetFavorite || false
          state.isGroupPhotoViewLimit =
            action.payload.isGroupPhotoViewLimit || false
          state.loading = false
        }
      })
      .addCase(fetchPhotosV2.rejected, (state) => {
        state.loading = false
      })
      // 处理 fetchMorePhotosV2
      .addCase(fetchMorePhotosV2.fulfilled, (state, action) => {
        state.photos.push(...action.payload.photos)
        state.lastPageCond = action.payload.lastPageCond
      })
      .addCase(fetchMorePhotosV2.rejected, (state) => {
        // 加载更多失败时不做处理，保持当前状态
      })
      // 处理 fetchSuggestedPhotosV2
      .addCase(fetchSuggestedPhotosV2.fulfilled, (state, action) => {
        state.suggestedPhotos = action.payload.photos
        state.suggestedPhotosNum = action.payload.total
        state.suggestedLastPageCond = action.payload.lastPageCond
      })
      // 处理 fetchMoreSuggestedPhotosV2
      .addCase(fetchMoreSuggestedPhotosV2.fulfilled, (state, action) => {
        state.suggestedPhotos.push(...action.payload.photos)
        state.suggestedLastPageCond = action.payload.lastPageCond
      })
      // 处理 fetch90DaysPhotosCountV2
      .addCase(fetch90DaysPhotosCountV2.fulfilled, (state, action) => {
        state.oldPhotosCount = action.payload.count || 0
      })
  }
})

// 导出 actions
export const {
  setIsAscSort,
  setPhotos,
  appendPhotos,
  setExpandInfo,
  setPhotosNum,
  setLastPageCond,
  setCanFavorite,
  setIsGroupPhotoViewLimit,
  setOldPhotosCount,
  setPhotoRectification,
  appendLabelsToPhotos,
  updateLabelsAcrossPhotos,
  pushCheckedPhoto,
  removeCheckedPhoto,
  pushUncheckedPhoto,
  removeUncheckedPhoto,
  setCheckAll,
  clearCheck,
  reset,
  updatePhotoFavorite,
  setSuggestedWords,
  updateSuggestedWordChecked,
  pushSuggestedCheckedPhoto,
  removeSuggestedCheckedPhoto,
  pushSuggestedUncheckedPhoto,
  removeSuggestedUncheckedPhoto,
  setSuggestedCheckAll,
  clearSuggestedCheck,
  setFallbackType,
  setFallbackKeyword,
  setSuggestedQueryFilter,
  setSuggestedPhotos
} = photosByContentSlice.actions

// 导出 selectors
export const selectIsAscSort = (state: RootState) =>
  state.photosByContent?.isAscSort || false
export const selectPhotos = (state: RootState) =>
  state.photosByContent?.photos || []
export const selectPhotosNum = (state: RootState) =>
  state.photosByContent?.photosNum || 0
export const selectLastPageCond = (state: RootState) =>
  state.photosByContent?.lastPageCond || ''
export const selectExpandInfo = (state: RootState) =>
  state.photosByContent?.expandInfo || null
export const selectCheckedPhotos = (state: RootState) =>
  state.photosByContent?.checkedPhotos || new Set<number>()
export const selectUncheckedPhotos = (state: RootState) =>
  state.photosByContent?.unCheckedPhotos || new Set<number>()
export const selectCheckAll = (state: RootState) =>
  state.photosByContent?.checkAll || false
export const selectCanFavorite = (state: RootState) =>
  state.photosByContent?.canFavorite || false
export const selectLoading = (state: RootState) =>
  state.photosByContent?.loading || false
export const selectIsGroupPhotoViewLimit = (state: RootState) =>
  state.photosByContent?.isGroupPhotoViewLimit || false
export const selectOldPhotosCount = (state: RootState) =>
  state.photosByContent?.oldPhotosCount || 0

// 猜你想搜 selectors
export const selectSuggestedWords = (state: RootState) =>
  state.photosByContent?.suggestedWords || []
export const selectSuggestedPhotos = (state: RootState) =>
  state.photosByContent?.suggestedPhotos || []
export const selectSuggestedPhotosNum = (state: RootState) =>
  state.photosByContent?.suggestedPhotosNum || 0
export const selectSuggestedLastPageCond = (state: RootState) =>
  state.photosByContent?.suggestedLastPageCond || ''
export const selectSuggestedCheckedPhotos = (state: RootState) =>
  state.photosByContent?.suggestedCheckedPhotos || new Set<number>()
export const selectSuggestedUncheckedPhotos = (state: RootState) =>
  state.photosByContent?.suggestedUnCheckedPhotos || new Set<number>()
export const selectSuggestedCheckAll = (state: RootState) =>
  state.photosByContent?.suggestedCheckAll || false
export const selectFallbackType = (state: RootState) =>
  state.photosByContent?.fallbackType || 'none'
export const selectFallbackKeyword = (state: RootState) =>
  state.photosByContent?.fallbackKeyword || ''
export const selectSuggestedQueryFilter = (state: RootState) =>
  state.photosByContent?.suggestedQueryFilter || null

// 导出 reducer
export default photosByContentSlice.reducer
